import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { Login } from './auth/Login';
import { ProtectedContent } from './auth/ProtectedContent';
import { ReportContent } from './auth/ReportContent';
import AboutUs from './pages/AboutUs';
import ActorMode from './pages/ActorMode';
import ActorModeVessel from './pages/ActorModeVessel';
import Admin from './pages/Admin';
import Careers from './pages/Careers';
import ClearViewReport from './pages/ClearViewReport';
import Landing from './pages/Front/Landing';
import PFReports from './pages/Home/all-reports/all-reports.tsx';
import HomeLayout from './pages/Home/Home.tsx';
import OverwatchReports from './pages/OverwatchReports';
import PathfinderReports from './pages/PathfinderReports';
import PressReleasePage from './pages/PressRelease';
import { PrivacyStatement } from './pages/PrivacyStatement.tsx';
import RecentInsightDashboard from './pages/RecentInsightDashboard';
import OrderOverwatchReport from './pages/Report/Build/OrderOverwatchReport';
import OrderPathfinderReport from './pages/Report/Build/OrderPathfinderReport';
import RequestReport from './pages/Report/Build/RequestReport';
import { InteractiveReport } from './pages/Report/interactiveReport/Overwatch';
import Overwatch from './pages/Report/interactiveReport/overwatch-2';
import { PathfinderReport } from './pages/Report/interactiveReport/PathfinderReport';
import SearchComponent from './pages/SearchComponent';
import { Terms } from './pages/Terms';
import VisualizeCandidates from './pages/VisualizeCandidates';
import { CookieConsent } from './shared/components/CookieConsent';
import Footer from './shared/components/Footer';
import Navbar from './shared/components/Navbar';
import StoryTeller from './shared/components/storytelling/Storyteller';
import { studioBaseUrl } from './utils/env.ts';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function ExternalRedirect({ to }) {
    window.location.assign(to);
    return null;
}

function App() {
    const location = useLocation();

    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <CookieConsent />
                <Navbar />
                <SentryRoutes>
                    <Route path="/report-builder" element={<RequestReport />} />
                    <Route path="/privacy-policy" element={<Terms />} />
                    <Route path="/privacy-statement" element={<PrivacyStatement />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/careers" element={<Careers />} />
                    {/* <Route path="/card" element={<CardPage />} /> */} {/*/card now redirects to main page */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/press-release" element={<PressReleasePage />} />
                    <Route element={<ProtectedContent />}>
                        <Route path="/home/*" element={<HomeLayout />}>
                            <Route path="all-reports" element={<PFReports />} />
                        </Route>
                        <Route path="/overwatch-reports" element={<OverwatchReports />} />
                        <Route path="/pathfinder-reports" element={<PathfinderReports />} />
                        <Route path="/order-overwatch-report" element={<OrderOverwatchReport />} />
                        <Route path="/order-pathfinder-report" element={<OrderPathfinderReport />} />
                        <Route path="/admin" element={<Admin />} />
                        <Route path="/visualize_candidates" element={<VisualizeCandidates />} />
                        <Route path="/clearview" element={<ClearViewReport />} />
                        <Route path="/recent_insight_dashboard" element={<RecentInsightDashboard />} />
                        <Route path="/actor/:reportUuid" element={<ActorMode />} />
                        <Route path="/actor/:reportUuid/vessel/:insightUuid" element={<ActorModeVessel />} />
                        <Route path="/v2/overwatch/:reportUuid" element={<Overwatch />} />
                        <Route element={<ReportContent />}>
                            <Route path="/story/:id" element={<StoryTeller />} />
                            <Route path="/pathfinder/:id" element={<PathfinderReport />} />
                            <Route path="/demo/pathfinder/:id" element={<PathfinderReport />} />
                            <Route path="/overwatch/:id" element={<InteractiveReport />} />
                            <Route path="/demo/overwatch/:id" element={<InteractiveReport />} />
                        </Route>
                        <Route path="/search" element={<SearchComponent />} />
                        <Route path="/user" element={<ExternalRedirect to={studioBaseUrl()} />} />
                    </Route>
                    <Route
                        path="*"
                        element={
                            <div className="bg-grey-1000">
                                <Landing />
                            </div>
                        }
                    />
                </SentryRoutes>
                {!(
                    location.pathname.startsWith('/report') |
                    location.pathname.startsWith('/story') |
                    location.pathname.startsWith('/pathfinder') |
                    location.pathname.startsWith('/overwatch') |
                    location.pathname.startsWith('/visualize_candidates') |
                    location.pathname.startsWith('/admin/order-pathfinder-report') |
                    location.pathname.startsWith('/order-overwatch-report') |
                    location.pathname.startsWith('/clearview') |
                    location.pathname.startsWith('/recent_insight_dashboard') |
                    location.pathname.startsWith('/actor') |
                    location.pathname.startsWith('/search') |
                    location.pathname.startsWith('/v2/overwatch') |
                    location.pathname.startsWith('/home')
                ) && <Footer />}
            </AuthProvider>
            {/* <ReactQueryDevtools initialIsOpen position="top-left" /> */}
        </QueryClientProvider>
    );
}

export default App;
